<template>
  <div>
    <ActionsMenu
      :actions="actions"
      :options="options"
      @view-on-schedule="onViewOnSchedule"
      @view-customer="onViewCustomer"
      @view-appointment="onViewAppointment"
      @edit-appointment="openEditDrawer"
      @cancel-appointment="openCancelDrawer"
    >
      <template #trigger>
        <slot name="trigger"></slot>
      </template>
    </ActionsMenu>
    <AppointmentDetailsDrawer
      v-if="drawerOpen"
      :appointment-id="appointment.id"
      :initial-cancel="cancelMode"
      @hide-drawer="onDrawerClosed"
    />
  </div>
</template>

<script setup lang="ts">
import moment from "moment";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { getQueryVariables } from "@/lib/url.js";

import ActionsMenu from "@/components/ActionsMenu.vue";
import AppointmentDetailsDrawer from "@/AppointmentDetails/AppointmentDetailsDrawer.vue";
import CONSTANTS from "@/Constants.js";
import {
  jobShowPath,
  scheduleBoardAppointmentModal,
  userShowPath
} from "@/mixins/path_helpers";
import { Appointment } from "@/typings/appointment";
import { Customer } from "@/typings/customer";

const { APPOINTMENT_STATUSES } = CONSTANTS;
const router = useRouter();

const emit = defineEmits(["save-success"]);
const props = defineProps<{
  viewOnSchedule?: boolean;
  options?: Record<string, unknown>;
  autoOpen?: boolean;
  appointment: Appointment;
  customer: Customer;
}>();
const drawerOpen = ref(false);
const cancelMode = ref(false);
const actions = computed(() => [
  {
    label: "Edit Appointment",
    icon: "far fa-pen-to-square",
    eventName: "edit-appointment"
  },
  {
    label: "Cancel Appointment",
    icon: "far fa-calendar-xmark",
    eventName: "cancel-appointment",
    disabled: [
      APPOINTMENT_STATUSES.cancelled,
      APPOINTMENT_STATUSES.completed,
      APPOINTMENT_STATUSES.running
    ].includes(props.appointment.status)
  },
  {
    label: "View on Schedule",
    icon: "far fa-calendar",
    eventName: "view-on-schedule",
    visible: props.viewOnSchedule,
    disabled: props.appointment.status === "cancelled"
  },
  {
    label: "View Appointment",
    icon: "far fa-calendar",
    eventName: "view-appointment",
    visible: !props.viewOnSchedule
  },
  {
    label: "View Customer",
    icon: "far fa-user-large",
    eventName: "view-customer"
  }
]);
const scheduleDate = computed(() => {
  // Get appointment scheduled date in correct format for schedule board
  return moment(
    props.appointment.iso_date_time || props.appointment.scheduled_time
  ).format(CONSTANTS.DATE_YEAR_CAL_FORMAT);
});

function onViewOnSchedule() {
  router.push(
    scheduleBoardAppointmentModal(props.appointment.id, scheduleDate.value)
  );
}

function onViewCustomer() {
  router.push(userShowPath(props.customer.id));
}

function onViewAppointment() {
  router.push(jobShowPath(props.appointment.job_id, props.appointment.id));
}

function openEditDrawer() {
  cancelMode.value = false;
  drawerOpen.value = true;
}

function openCancelDrawer() {
  cancelMode.value = true;
  drawerOpen.value = true;
}

function onDrawerClosed() {
  drawerOpen.value = false;
  cancelMode.value = false;
  emit("save-success");
}

onMounted(() => {
  if (props.autoOpen) {
    const { appointment_id } = getQueryVariables();
    if (appointment_id === props.appointment.id?.toString()) {
      drawerOpen.value = true;
    }
  }
});
</script>

<style scoped></style>
